<template>
  <v-dialog
    persistent
    v-model="visible"
    scrollable
    :fullscreen="isMobile"
    :max-width="isMobile ? '480px' : '500px'"
    :content-class="isMobile ? 'tips-modal isMobile' : 'tips-modal'"
    :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'"
  >
    <template v-slot:activator="{ on }">
      <TipForDriver :order="order" :onTip="on" />
    </template>
    <v-card color="#f2f2f2">
      <v-toolbar card color="white">
        <v-btn icon @click="closeModal" v-if="isMobile">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title :class="{ 'flex grow ml-0 pr-4 text-xs-center': isMobile }">
          {{ $t('record.tip_for_driver.heading') }}
        </v-toolbar-title>
        <template v-if="!isMobile">
          <v-spacer></v-spacer>
          <v-btn icon @click="closeModal">
            <v-icon>close</v-icon>
          </v-btn>
        </template>
      </v-toolbar>

      <v-card-text
        :style="{
          overflowY: 'visible',
          paddingTop: isMobile ? '16px' : '0',
          marginTop: isMobile ? '1px' : '0'
        }"
      >
        <div class="d-flex flex-no-wrap justify-space-between align-center">
          <div class="pr-4">
            <h3>{{ $t('record.tip_for_driver.title') }}</h3>
            <p class="grey--text text--darken-1 mb-0">
              {{ $t('record.tip_for_driver.desc') }}
            </p>
          </div>
          <div>
            <v-img
              contain
              src="/assets/images/tip_for_driver.png"
              width="74"
              class="ml-auto"
            ></v-img>
          </div>
        </div>
      </v-card-text>
      <v-card-text class="mt-3" style="padding: 12px 16px">
        <h3 class="subheading font-weight-bold">
          {{ $t('record.tip_for_driver.tip_by') }}
        </h3>
        <v-radio-group v-model="tipType" row @change="handleChangeTipType">
          <v-radio
            :label="$t('record.tip_for_driver.options')"
            :value="$constants.TIP_TYPE.FLAT"
          ></v-radio>
          <v-radio
            :label="$t('record.tip_for_driver.total_order_value')"
            :value="$constants.TIP_TYPE.PERCENTAGE"
          ></v-radio>
        </v-radio-group>
        <v-container v-bind="{ [`grid-list-lg`]: true }" pa-0 mb-3 fluid style="min-height: 107px">
          <v-layout row wrap>
            <v-flex
              v-for="option in tipOptions"
              :key="option.name"
              :xs4="tipType === $constants.TIP_TYPE.FLAT"
              :xs3="tipType === $constants.TIP_TYPE.PERCENTAGE && !isMobile"
              :xs6="tipType === $constants.TIP_TYPE.PERCENTAGE && isMobile"
            >
              <v-btn
                :ripple="false"
                flat
                v-if="option.enable"
                :class="{
                  active: option.is_manual_input
                    ? selectedTipOptionName === option.name &&
                      manualTipAmount &&
                      manualTipAmount > 0
                    : selectedTipOptionName === option.name,
                  'tips-modal__option text-none': true
                }"
                @click="handleSelectTipOption(option)"
              >
                <v-btn
                  color="white"
                  fab
                  small
                  class="tips-modal__option--editable"
                  v-if="option.is_manual_input && manualTipAmount && manualTipAmount > 0"
                  @click="showManualTipModal"
                >
                  <v-icon small>border_color</v-icon>
                </v-btn>
                <v-img
                  v-if="option.image_url"
                  :src="option.image_url"
                  :width="40"
                  height="auto"
                  class="mx-auto"
                ></v-img>
                <v-card-title>
                  <template v-if="option.is_manual_input">
                    <vue-numeric
                      v-if="manualTipAmount"
                      :currency="$constants.CURRENCY_CONFIG.prefix"
                      :value="manualTipAmount"
                      read-only
                      name="manual_tip_amount"
                    ></vue-numeric>
                    <span v-else>{{ $t('record.tip_for_driver.enter_tip') }}</span>
                  </template>
                  <span v-else-if="tipType === $constants.TIP_TYPE.PERCENTAGE">
                    <vue-numeric
                      :value="option.value * 100"
                      read-only
                      name="tip_amount_percentage"
                    ></vue-numeric
                    >%
                  </span>
                  <vue-numeric
                    v-else
                    :currency="$constants.CURRENCY_CONFIG.prefix"
                    :value="option.value"
                    read-only
                    name="tip_amount"
                  ></vue-numeric>
                </v-card-title>
              </v-btn>
            </v-flex>
            <v-flex
              xs12
              class="caption font-weight-medium grey--text text--darken-1"
              v-if="tipType === $constants.TIP_TYPE.PERCENTAGE"
            >
              <span class="mr-1">{{ $t('record.tip_for_driver.you_will_tip') }}</span>
              <span class="body-2 font-weight-bold grey--text text--darken-3">
                <vue-numeric
                  :currency="$constants.CURRENCY_CONFIG.prefix"
                  :value="estimatedTipByPercentage"
                  read-only
                  name="will_tip_amount"
                ></vue-numeric>
              </span>
            </v-flex>
          </v-layout>
        </v-container>
        <v-flex xs12 mb-3>
          <ATextField
            data-test-id="tipNote"
            :label="$t('record.tip_for_driver.message')"
            type="text"
            :value="note"
            :rules="[inputRules.note]"
            @onChange="(value) => handleChange('note', value)"
            @onBlur="onBlurTextField('note')"
            @onError="handleNoteError"
          />
        </v-flex>
        <h5 class="caption font-weight-bold uppercase mb-1">
          {{ $t('record.tip_for_driver.note') }}
        </h5>
        <p class="caption font-weight-medium grey--text text--darken-1 mb-0">
          {{ $t('record.tip_for_driver.note_info') }}
        </p>
      </v-card-text>

      <v-card-actions :class="{ 'elevation-16': isMobile, 'pa-3': true }">
        <v-btn
          @click="goToCheckoutPage"
          block
          large
          depressed
          color="accent"
          data-test-id="SELECT-BTN"
          class="text-none"
          :disabled="!tipValue"
        >
          {{ $t('record.tip_for_driver.send_tip') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <ManualTipModal
      ref="manualTipModal"
      :initialValue="manualTipAmount ? manualTipAmount.toString() : null"
      @onSave="handleSaveManualTip"
    />
  </v-dialog>
</template>

<script>
import { getTips } from '@/store/api/common'
import { mapGetters } from 'vuex'
import TipForDriver from './TipForDriver.vue'
import ManualTipModal from './ManualTipModal.vue'
import ATextField from '@/components/ahamove/ATextField'
import { EVENTS } from '@/mixins/tracking-events'

export default {
  name: 'TipForDriverModal',
  components: {
    ATextField,
    TipForDriver,
    ManualTipModal
  },
  props: {
    order: {
      type: Object
    }
  },
  data() {
    return {
      visible: false,
      loadingTipOptions: false,
      tipType: this.$constants.TIP_TYPE.FLAT,
      tipOptionsByFlat: [],
      tipOptionsByPercentage: [],
      selectedTipOptionName: null,
      manualTipAmount: null,
      note: null,
      inputRules: {
        note: (v) =>
          !v || (v && v.length <= 120) || this.$t('record.tip_for_driver.note_error_message')
      },
      errors: []
    }
  },
  computed: {
    ...mapGetters(['localeSuffix']),
    tipOptions() {
      return this.tipType === this.$constants.TIP_TYPE.FLAT
        ? this.tipOptionsByFlat
        : this.tipOptionsByPercentage
    },
    estimatedTipByPercentage() {
      const selectedItem = this.tipOptionsByPercentage.find(
        (t) => t.name === this.selectedTipOptionName
      )
      return selectedItem ? selectedItem.value * this.order.total_fee : 0
    },
    selectedTipOption() {
      if (!this.selectedTipOptionName) return

      if (this.tipType === this.$constants.TIP_TYPE.FLAT) {
        return this.tipOptionsByFlat.find((t) => t.name === this.selectedTipOptionName)
      }

      return this.tipOptionsByPercentage.find((t) => t.name === this.selectedTipOptionName)
    },
    tipValue() {
      if (!this.selectedTipOption) return

      return this.selectedTipOption.is_manual_input
        ? parseFloat(this.manualTipAmount || '')
        : this.selectedTipOption.value
    }
  },
  methods: {
    closeModal() {
      this.visible = false
    },
    goToCheckoutPage() {
      if (!this.selectedTipOption) return

      this.$store
        .dispatch('setTipInfo', {
          type: this.tipType,
          value: this.tipValue,
          description: this.note
        })
        .then(() => {
          this.$analyticTracking.trackEvent(EVENTS.TIP_OPTIONSCREEN_CONFIRM, {
            order_status: this.order.status,
            order_fee: this.order.total_fee,
            service_id: this.order.service_id,
            tip_type: this.tipType,
            value: this.tipValue,
            comment: this.note
          }),
            this.$router.push({
              name: 'checkout',
              params: {
                id: this.order._id
              },
              query: { ...this.$route.query, payment_type: this.$constants.PAYMENT_TYPE.TIP }
            })
        })
    },
    showToolTip(title) {
      this.$store.dispatch('setSnackbar', {
        visible: true,
        title,
        timeout: 1500
      })
    },
    getTipOptionsByType() {
      const params = {
        type: this.tipType
      }
      this.loadingTipOptions = true
      getTips(params)
        .then(({ data }) => {
          if (this.tipType === this.$constants.TIP_TYPE.FLAT) {
            this.tipOptionsByFlat = data
          } else {
            this.tipOptionsByPercentage = data
          }
        })
        .catch((err) => {
          if (err && err.error) {
            const { title, description } = err.error
            this.showMessage(description || title)
          }
        })
        .finally(() => {
          this.loadingTipOptions = false
        })
    },
    handleChangeTipType() {
      this.selectedTipOptionName = null
    },
    handleSelectTipOption(option) {
      this.handleChange('selectedTipOptionName', option.name)
      if (option.is_manual_input && !this.manualTipAmount) {
        this.showManualTipModal()
      }
    },
    handleChange(field, value) {
      this[field] = value
    },
    onBlurTextField(value) {
      if (this[value] === null || this[value] === undefined) {
        this[value] = ''
      }
    },
    checkValidateData() {
      this.onBlurTextField('note', false)
      if (!this.note) return false
      return true
    },
    handleNoteError(value) {
      let data = true
      if (value) {
        data = 'error-message'
      }

      this.handleError('note', data)
    },
    handleError(field, data) {
      /**
       * [data]
       * @type {Boolean} true - no error
       * @type {String} String of error message
       */
      if (field) {
        if (data === true) {
          const index = this.errors.findIndex((e) => e.key === field)
          this.errors.splice(index, 1)
        } else {
          this.errors.push({
            key: field,
            value: data
          })
        }
      }
    },
    handleSaveManualTip(value) {
      this.manualTipAmount = value
    },
    showManualTipModal() {
      if (this.$refs.manualTipModal) {
        this.$refs.manualTipModal.dialog = true
      }
    }
  },
  mounted() {},
  created() {
    this.getTipOptionsByType()
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.$analyticTracking.trackEvent(EVENTS.TIP_ENTRYPOINT_BTN, {
          order_status: this.order.status,
          order_fee: this.order.total_fee,
          service_id: this.order.service_id
        })
      }
    },
    tipType(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (
          this.tipType === this.$constants.TIP_TYPE.FLAT &&
          this.tipOptionsByFlat &&
          this.tipOptionsByFlat.length <= 0
        ) {
          this.getTipOptionsByType()
        }

        if (
          this.tipType === this.$constants.TIP_TYPE.PERCENTAGE &&
          this.tipOptionsByPercentage &&
          this.tipOptionsByPercentage.length <= 0
        ) {
          this.getTipOptionsByType()
        }
      }
    }
  }
}
</script>

<style lang="scss">
.tips-modal {
  &__option {
    border: 1px solid #c1c7cd !important;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    margin: 0;
    position: relative;

    &--editable {
      &.v-btn {
        height: 36px;
        width: 36px;
        margin: 6px;
        position: absolute !important;
        top: 0;
        right: -16px;
        z-index: 10;
      }
    }

    .v-btn__content {
      display: flex;
      flex-direction: column;
    }

    &.active {
      background: #fff2ea;
      border-color: #fe5f00 !important;
      color: #fe5f00;

      &:hover {
        &::before {
          background: #fff2ea;
        }
      }
    }

    .v-image {
      margin: 16px auto -4px;
    }

    .v-card__title {
      justify-content: center;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
    }
  }

  .a-text-field {
    .v-input__slot {
      border: 1px solid #c1c7cd !important;
      border-radius: 3px;
    }

    &.error--text {
      .v-input__slot {
        border-color: #d44632 !important;
      }
    }
  }

  > .v-card {
    > .v-toolbar {
      z-index: 10;
    }

    .v-card {
      &__text {
        background: #fff;

        .v-input--selection-controls {
          margin-top: 12px;

          label {
            font-weight: 600;
            font-size: 14px;
            line-height: 20px;
            color: #343a3f;
          }
        }
      }

      &__actions {
        margin-top: auto;
        flex: none;
        background: #fff;
        z-index: 1;
      }
    }
  }
}
</style>
